import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  IconButton,
  Paper,
  Avatar,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  Send as SendIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Chat as ChatIcon,
} from '@mui/icons-material';
import {
  portalApi,
  PortalTicket,
  PortalNachricht,
  CreatePortalTicketDto,
} from '../../services/portalService';

const prioritaeten = ['Niedrig', 'Normal', 'Hoch'];

const PortalAuftraegePage: React.FC = () => {
  const [tickets, setTickets] = useState<PortalTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Dialog für neuen Auftrag
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newTicket, setNewTicket] = useState<CreatePortalTicketDto>({
    titel: '',
    beschreibung: '',
    prioritaet: 'Normal',
  });
  const [creating, setCreating] = useState(false);

  // Detail-Dialog
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<PortalTicket | null>(null);
  const [nachrichten, setNachrichten] = useState<PortalNachricht[]>([]);
  const [loadingNachrichten, setLoadingNachrichten] = useState(false);
  const [neueNachricht, setNeueNachricht] = useState('');
  const [sendingNachricht, setSendingNachricht] = useState(false);

  useEffect(() => {
    loadTickets();
  }, []);

  const loadTickets = async () => {
    try {
      setLoading(true);
      const response = await portalApi.getMeineTickets();
      setTickets(response.data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Fehler beim Laden der Aufträge');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTicket = async () => {
    if (!newTicket.titel.trim()) return;

    try {
      setCreating(true);
      const response = await portalApi.createTicket(newTicket);
      setTickets([response.data, ...tickets]);
      setCreateDialogOpen(false);
      setNewTicket({ titel: '', beschreibung: '', prioritaet: 'Normal' });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Fehler beim Erstellen des Auftrags');
    } finally {
      setCreating(false);
    }
  };

  const handleOpenDetail = async (ticket: PortalTicket) => {
    setSelectedTicket(ticket);
    setDetailDialogOpen(true);
    setLoadingNachrichten(true);

    try {
      const response = await portalApi.getTicketNachrichten(ticket.ticketID);
      setNachrichten(response.data);
    } catch (err) {
      console.error('Fehler beim Laden der Nachrichten:', err);
      setNachrichten([]);
    } finally {
      setLoadingNachrichten(false);
    }
  };

  const handleSendNachricht = async () => {
    if (!selectedTicket || !neueNachricht.trim()) return;

    try {
      setSendingNachricht(true);
      const response = await portalApi.sendeTicketNachricht(selectedTicket.ticketID, {
        nachricht: neueNachricht,
      });
      setNachrichten([...nachrichten, response.data]);
      setNeueNachricht('');
    } catch (err: any) {
      console.error('Fehler beim Senden:', err);
    } finally {
      setSendingNachricht(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'offen':
        return 'warning';
      case 'in bearbeitung':
        return 'info';
      case 'erledigt':
        return 'success';
      case 'geschlossen':
        return 'default';
      default:
        return 'default';
    }
  };

  const getPriorityColor = (priority: string | undefined) => {
    switch (priority?.toLowerCase()) {
      case 'hoch':
        return 'error';
      case 'mittel':
      case 'normal':
        return 'warning';
      case 'niedrig':
        return 'success';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('de-CH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Meine Aufträge</Typography>
        <Box display="flex" gap={1}>
          <IconButton onClick={loadTickets} color="primary">
            <RefreshIcon />
          </IconButton>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Neuer Auftrag
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {tickets.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="textSecondary" gutterBottom>
            Sie haben noch keine Aufträge
          </Typography>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
            sx={{ mt: 2 }}
          >
            Ersten Auftrag erstellen
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {tickets.map((ticket) => (
            <Grid item xs={12} md={6} lg={4} key={ticket.ticketID}>
              <Card
                sx={{
                  cursor: 'pointer',
                  '&:hover': { boxShadow: 4 },
                  transition: 'box-shadow 0.2s',
                }}
                onClick={() => handleOpenDetail(ticket)}
              >
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                    <Typography variant="h6" component="div" sx={{ fontSize: '1rem' }}>
                      {ticket.titel}
                    </Typography>
                    <Chip
                      size="small"
                      label={ticket.status}
                      color={getStatusColor(ticket.status) as any}
                    />
                  </Box>

                  {ticket.statusBeschreibung && (
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                      {ticket.statusBeschreibung}
                    </Typography>
                  )}

                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="caption" color="textSecondary">
                      Erstellt: {new Date(ticket.erstelltAm).toLocaleDateString('de-CH')}
                    </Typography>
                    {ticket.prioritaet && (
                      <Chip
                        size="small"
                        label={ticket.prioritaet}
                        color={getPriorityColor(ticket.prioritaet) as any}
                        variant="outlined"
                      />
                    )}
                  </Box>

                  {ticket.letzteAktualisierung && (
                    <Typography variant="caption" color="textSecondary" display="block">
                      Aktualisiert: {new Date(ticket.letzteAktualisierung).toLocaleDateString('de-CH')}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Dialog: Neuer Auftrag */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Neuen Auftrag erstellen</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Titel / Betreff"
              value={newTicket.titel}
              onChange={(e) => setNewTicket({ ...newTicket, titel: e.target.value })}
              fullWidth
              required
              placeholder="Worum geht es?"
            />
            <TextField
              label="Beschreibung"
              value={newTicket.beschreibung}
              onChange={(e) => setNewTicket({ ...newTicket, beschreibung: e.target.value })}
              multiline
              rows={4}
              fullWidth
              placeholder="Beschreiben Sie Ihr Anliegen..."
            />
            <TextField
              select
              label="Priorität"
              value={newTicket.prioritaet}
              onChange={(e) => setNewTicket({ ...newTicket, prioritaet: e.target.value })}
              fullWidth
            >
              {prioritaeten.map((p) => (
                <MenuItem key={p} value={p}>
                  {p}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Abbrechen</Button>
          <Button
            variant="contained"
            onClick={handleCreateTicket}
            disabled={!newTicket.titel.trim() || creating}
          >
            {creating ? <CircularProgress size={24} /> : 'Auftrag erstellen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Auftrags-Detail mit Nachrichten */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { minHeight: '60vh' } }}
      >
        {selectedTicket && (
          <>
            <DialogTitle>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography variant="h6">{selectedTicket.titel}</Typography>
                  <Box display="flex" gap={1} mt={1}>
                    <Chip
                      size="small"
                      label={selectedTicket.status}
                      color={getStatusColor(selectedTicket.status) as any}
                    />
                    {selectedTicket.prioritaet && (
                      <Chip
                        size="small"
                        label={selectedTicket.prioritaet}
                        color={getPriorityColor(selectedTicket.prioritaet) as any}
                        variant="outlined"
                      />
                    )}
                  </Box>
                </Box>
                <IconButton onClick={() => setDetailDialogOpen(false)}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column' }}>
              {/* Status-Info */}
              {selectedTicket.statusBeschreibung && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {selectedTicket.statusBeschreibung}
                </Alert>
              )}

              {/* Nachrichten-Bereich */}
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <ChatIcon color="primary" />
                <Typography variant="subtitle1" fontWeight="bold">
                  Kommunikation
                </Typography>
              </Box>

              {loadingNachrichten ? (
                <Box display="flex" justifyContent="center" py={3}>
                  <CircularProgress size={30} />
                </Box>
              ) : nachrichten.length === 0 ? (
                <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', mb: 2 }}>
                  <Typography color="textSecondary">
                    Noch keine Nachrichten. Schreiben Sie uns bei Fragen.
                  </Typography>
                </Paper>
              ) : (
                <Box
                  sx={{
                    flex: 1,
                    overflow: 'auto',
                    mb: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    maxHeight: 300,
                  }}
                >
                  {nachrichten.map((nachricht) => (
                    <Box
                      key={nachricht.portalNachrichtID}
                      sx={{
                        display: 'flex',
                        justifyContent: nachricht.vomPortalBenutzer ? 'flex-end' : 'flex-start',
                      }}
                    >
                      <Paper
                        elevation={1}
                        sx={{
                          p: 2,
                          maxWidth: '80%',
                          bgcolor: nachricht.vomPortalBenutzer ? 'primary.light' : 'grey.100',
                          color: nachricht.vomPortalBenutzer ? 'primary.contrastText' : 'text.primary',
                        }}
                      >
                        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                          <Avatar sx={{ width: 20, height: 20, fontSize: '0.7rem' }}>
                            {nachricht.vomPortalBenutzer ? <PersonIcon fontSize="small" /> : <BusinessIcon fontSize="small" />}
                          </Avatar>
                          <Typography variant="caption" fontWeight="bold">
                            {nachricht.vomPortalBenutzer ? 'Sie' : 'Mitarbeiter'}
                          </Typography>
                          <Typography variant="caption">
                            {formatDate(nachricht.gesendetAm)}
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {nachricht.inhalt}
                        </Typography>
                      </Paper>
                    </Box>
                  ))}
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              {/* Neue Nachricht */}
              <Box display="flex" gap={1}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Nachricht schreiben..."
                  value={neueNachricht}
                  onChange={(e) => setNeueNachricht(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendNachricht();
                    }
                  }}
                  multiline
                  maxRows={3}
                />
                <Button
                  variant="contained"
                  onClick={handleSendNachricht}
                  disabled={!neueNachricht.trim() || sendingNachricht}
                  sx={{ minWidth: 100 }}
                >
                  {sendingNachricht ? <CircularProgress size={24} /> : <SendIcon />}
                </Button>
              </Box>
            </DialogContent>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default PortalAuftraegePage;
